import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  SettingsOutlined as SettingsIcon,
  CheckCircleOutline as ActiveIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as ActivateIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import PageHeader from '../../../../components/venue/PageHeader';
import itemCostLayerService, { type CostingStatus } from '../../../../services/itemCostLayerService';
import CostTrackingHelpModal from '../../../../components/venue/CostTrackingHelpModal';

/**
 * COSTING.1a-web (E6) / COSTING.1c — Inventory settings (ADMIN-only route).
 *
 * The costing method (FIFO — a fixed fact) and the venue's cost-tracking
 * activation status. When dormant, an ADMIN can ACTIVATE cost tracking here
 * (COSTING.1c) — a deliberate, one-way action valuing current stock at last
 * known cost. NO cost-visibility switch yet (COSTING.1d).
 */

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 3,
        py: 2,
        px: 3,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160, pt: 0.25 }}>{label}</Typography>
      <Box sx={{ textAlign: 'right', flex: 1 }}>{children}</Box>
    </Box>
  );
};

const InventorySettings: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();
  const backPath = `/admin/venues/${venueId}/inventory`;

  const [status, setStatus] = useState<CostingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activating, setActivating] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      setStatus(await itemCostLayerService.getCostingStatus(venueId));
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to load inventory settings.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { void load(); }, [load]);

  const handleActivate = async () => {
    if (!venueId) return;
    setActivating(true);
    setError(null);
    try {
      const result = await itemCostLayerService.activateCosting(venueId);
      setConfirmOpen(false);
      setSnack(
        result.alreadyActive
          ? 'Cost tracking was already active for this venue.'
          : `Cost tracking activated — ${result.layersCreated} cost layer${result.layersCreated === 1 ? '' : 's'} created from current stock.`,
      );
      await load(); // status row flips to "Active since …"
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to activate cost tracking.');
      setConfirmOpen(false);
    } finally {
      setActivating(false);
    }
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        backLink={{ label: 'Inventory', to: backPath }}
        icon={<SettingsIcon />}
        title="Inventory settings"
        subtitle="How this venue values and tracks the cost of stock. These settings are read-only for now — cost tracking is activated by an administrator in a later phase."
      />

      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
            <Typography variant="subtitle1" fontWeight={600}>Costing</Typography>
          </Box>

          <Row label="Costing method">
            <Typography variant="body2" fontWeight={600}>FIFO — first in, first out</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Stock is valued in the order it was received: the oldest cost on hand is used first as stock is consumed.
            </Typography>
          </Row>

          <Row label="Cost tracking">
            {status?.active ? (
              <>
                <Chip
                  size="small"
                  icon={<ActiveIcon sx={{ fontSize: 16 }} />}
                  label="Active"
                  sx={{ fontWeight: 600, color: '#1a7f37', backgroundColor: '#dafbe1', border: 'none' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {status.activatedAt ? `Active since ${fmtDate(status.activatedAt)}` : 'Active'}
                </Typography>
              </>
            ) : (
              <>
                <Chip
                  size="small"
                  icon={<PendingIcon sx={{ fontSize: 16 }} />}
                  label="Not yet active"
                  sx={{ fontWeight: 600, color: '#6e7781', backgroundColor: '#f6f8fa', border: 'none' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1 }}>
                  Start cost tracking to value stock as it moves. Current stock on hand is valued at last known cost as a starting point.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ActivateIcon />}
                  onClick={() => setConfirmOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Activate cost tracking
                </Button>
              </>
            )}
          </Row>
        </Paper>
      )}

      {/* COSTING.1d — "About cost tracking" reference panel (plain-language). */}
      {!loading && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={600}>About cost tracking</Typography>
            {/* COST-GUIDE.1 — opens the full cost guide. */}
            <Tooltip title="How cost tracking works" arrow>
              <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: 'text.secondary' }}>
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography variant="body2" paragraph>
              Cost tracking follows the money in your stock. Each time you receive, transfer, or use
              stock, the system records what that specific batch actually cost — so your reports show
              real cost, not just a current estimate.
            </Typography>
            <Typography variant="body2" paragraph>
              Stock is valued oldest first: the earliest units you bought are used up first, at the
              price you paid for them (first in, first out). That mirrors how stock actually moves, so
              the value on hand reflects what you genuinely still have.
            </Typography>
            <Typography variant="body2">
              {status?.active
                ? `Activation valued the stock you had on hand at its last known cost as a starting point, and every movement since has recorded its own cost. Figures dated before activation are shown as estimates.`
                : `Until an administrator activates cost tracking, reports value stock at the current last known cost. Activation sets a starting point from today's stock; it does not change past records and is permanent once started.`}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* COSTING.1c — activation confirm (one-way action; paper copy). */}
      <Dialog open={confirmOpen} onClose={activating ? undefined : () => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Activate cost tracking?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This starts cost tracking for this venue. Current stock on hand will be valued at
            last known cost as a starting point.
            <Box component="span" sx={{ display: 'block', mt: 1.5, fontWeight: 600, color: 'text.primary' }}>
              This cannot be undone.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={activating} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleActivate}
            disabled={activating}
            startIcon={activating ? <CircularProgress size={16} color="inherit" /> : <ActivateIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {activating ? 'Activating…' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <CostTrackingHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default InventorySettings;
